import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const HottestEvents = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const events = [
    {
      id: 7,
      title: "IPL 2026",
      caption: "The biggest cricket carnival returns. Experience the T20 extravaganza with exclusive corporate boxes, VIP hospitality, and meet-and-greet opportunities with your favourite cricketers.",
      pricing: "₹50,000 - ₹5,00,000",
      image: '/images/hottest-events/ipl_trophy.png',
      date: "Mar–May 2026",
      category: "Cricket"
    },
    {
      id: 2,
      title: "ICC Champions Trophy 2025",
      caption: "Witness India take on Pakistan and the world's best in cricket's most prestigious 50-over showdown. Premium hospitality packages with exclusive access.",
      pricing: "₹75,000 - ₹8,00,000",
      image: '/images/hottest-events/event_india_nz.png',
      date: "Feb–Mar 2025",
      category: "Cricket"
    },
    {
      id: 3,
      title: "FIFA World Cup 2026",
      caption: "Where passion fills the air. Experience the electricity of the crowd and the luxury of being treated like a VIP at the beautiful game's biggest stage.",
      pricing: "₹1,00,000 - ₹10,00,000",
      image: '/images/hottest-events/event_3.png',
      date: "Jun-Jul 2026",
      category: "Football"
    },
    {
      id: 4,
      title: "Formula 1 Racing",
      caption: "Speed, style, and pure adrenaline. Design your race-weekend with premium trackside hospitality and unbeatable views of motorsport's most glamorous stage.",
      pricing: "₹1,50,000 - ₹12,00,000",
      image: '/images/hottest-events/event_race.png',
      date: "2026 Season",
      category: "Motorsport"
    },
    {
      id: 6,
      title: "Wimbledon 2026",
      caption: "Experience the prestige of tennis's most iconic tournament. Exclusive early bird rates on premium court-side seating and hospitality packages.",
      pricing: "₹1,00,000 - ₹1,20,000",
      image: '/images/hottest-events/event_4.png',
      date: "Jun-Jul 2026",
      category: "Tennis"
    }
  ];

  const itemsPerView = 3;
  const maxIndex = Math.max(0, events.length - itemsPerView);

  // Auto-scroll functionality
  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isPaused, maxIndex]);

  const scrollToContact = () => {
    const contactElement = document.getElementById('contact');
    if (contactElement) {
      contactElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handlePrev = () => {
    setCurrentIndex(prev => (prev === 0 ? maxIndex : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
  };

  const getVisibleEvents = () => {
    const visible = [];
    for (let i = 0; i < itemsPerView; i++) {
      visible.push(events[(currentIndex + i) % events.length]);
    }
    return visible;
  };

  return (
    <section id="hottest-events" className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-l from-purple-200/20 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-r from-amber-200/20 to-transparent rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-6 mb-16">
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
            Hottest
            <span className="block bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent leading-tight">
              Events Right Now
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Discover exclusive experiences and premium access to the most sought-after events in India
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Cards Grid */}
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-500"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {getVisibleEvents().map((event, idx) => (
              <div
                key={`${event.id}-${idx}`}
                className="group relative h-full"
              >
                {/* Gradient Border */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/30 via-pink-500/30 to-orange-500/30 rounded-3xl blur-lg group-hover:blur-xl transition-all duration-300 opacity-0 group-hover:opacity-100"></div>

                {/* Card */}
                <div className="relative bg-white backdrop-blur-sm rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 h-full flex flex-col">
                  {/* Image Container */}
                  <div className="relative w-full h-64 overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {/* Overlay Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    {/* Category Badge */}
                    <div className="absolute top-4 right-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      {event.category}
                    </div>

                    {/* Date Badge */}
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur text-gray-900 px-3 py-1 rounded-full text-xs font-semibold">
                      {event.date}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-grow p-6 flex flex-col">
                    {/* Title */}
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 line-clamp-2">
                      {event.title}
                    </h3>

                    {/* Caption */}
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-grow line-clamp-3">
                      {event.caption}
                    </p>

                    {/* Pricing */}
                    <div className="mb-6 pb-6 border-t border-gray-200 pt-4">
                      <p className="text-sm text-gray-500 mb-1">Pricing Range</p>
                      <p className="text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                        {event.pricing}
                      </p>
                    </div>

                    {/* Register Button */}
                    <button
                      onClick={scrollToContact}
                      className="w-full bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 text-white font-semibold py-3 px-6 rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                    >
                      Register Interest
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          {events.length > itemsPerView && (
            <>
              <button
                onClick={handlePrev}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-16 bg-white shadow-lg hover:shadow-xl text-gray-900 p-3 rounded-full transition-all duration-300 hover:scale-110 z-20"
                aria-label="Previous events"
              >
                <ChevronLeft size={24} />
              </button>

              <button
                onClick={handleNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-16 bg-white shadow-lg hover:shadow-xl text-gray-900 p-3 rounded-full transition-all duration-300 hover:scale-110 z-20"
                aria-label="Next events"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {/* Dot Indicators */}
          {events.length > itemsPerView && (
            <div className="flex justify-center gap-2 mt-10">
              {Array.from({ length: Math.ceil(events.length / itemsPerView) }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx * itemsPerView)}
                  className={`h-2 rounded-full transition-all duration-300 ${idx === Math.floor(currentIndex / itemsPerView)
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 w-8'
                    : 'bg-gray-300 w-2 hover:bg-gray-400'
                    }`}
                  aria-label={`Go to event group ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
